/**
 * @file nationalLogistics.js
 * @version 2.4.0
 * @date March 15, 2026
 * @author Dr. Tatar Radu MD
 *
 * @description
 * National logistics standards with inheritance pattern.
 * NATO is the baseline - nations only override their differences.
 *
 * VERSION 2.4.0 CHANGES (March 15, 2026) — NS-17 FRENCH (Armée de Terre):
 * - NL-08: Added FRENCH to NATIONAL_LOGISTICS_OVERRIDES.
 *   Consumption rates: NATO-equivalent on all measurable parameters.
 *   Key doctrinal differentiator: France uses FLYP (French Lyophilized Plasma)
 *   instead of FFP. Same 1:1 ratio with RBC, but room-temperature storage,
 *   universal blood group, <6 min reconstitution. Product substitution only —
 *   no rate override required. Source: CTSA Clamart; Martinaud et al. Transfusion 2013.
 *   MDOS: NATO 20-day standard (3+4+13). No French deviation found in research.
 *   DOS thresholds: NATO defaults. No French override found.
 *   Logistics doctrine: BSL (Bataillon de Soutien Logistique) at brigade;
 *   B.LOG (Brigade Logistique, created March 2024) at corps/division;
 *   Groupement de Soutien de Corps (GSC) above.
 *   Blood source: Centre de Transfusion Sanguine des Armées (CTSA), Clamart.
 * - NL-09: Added FRENCH to AVAILABLE_NATIONAL_STANDARDS (between BRITISH and MULTINATIONAL).
 *   FLAG_ICONS.FRENCH (alias added to icons.js v2.3).
 *   shortLabel: 'FRA'. complete: true.
 *
 * VERSION 2.3.0 CHANGES (March 2, 2026) — ISSUE-01 Supply Scaling Fix:
 * - Added dressings_per_t1/t2/t3/dnbi to NATO_LOGISTICS_BASELINE.consumption_rates
 *   Source: TCCC CoTCCC 2024 guidelines; DNBI_CATEGORIES wound-requiring fraction
 *   No national overrides — wound physiology is universal
 * - Added surgical_kit_per_case: 1.0 (engineering identity — 1 sterile pack per DCS case)
 *   Source: US Army ATP 4-02.25 FST equipment tables
 * - NOTE: controlled_per_surgical (8.0 NATO, 12.0 US) already existed — this version
 *   enables it to flow into supplyChainModule calculateDailyConsumption()
 *
 * VERSION 2.2.0 CHANGES (February 26, 2026) — FIX_PLAN_LOGISTICS_DOS_v1_0:
 * - L1-01: Import FLAG_ICONS from utils/icons — APP_RULES 5.1
 * - L1-02: Replace raw emoji flags in AVAILABLE_NATIONAL_STANDARDS with FLAG_ICONS references
 * - L1-03: Add shortLabel field to all AVAILABLE_NATIONAL_STANDARDS entries (matches nationalStandards.js)
 * - L1-04: Add MULTINATIONAL entry to AVAILABLE_NATIONAL_STANDARDS
 * - L1-05: Add MULTINATIONAL entry to NATIONAL_LOGISTICS_OVERRIDES (inherits NATO baseline, no overrides)
 * - L1-06: Add tourniquet_per_t1/t2/t3, chest_seal_per_t1/t2/t3, morphine_per_t1/t2/t3
 *          to NATO_LOGISTICS_BASELINE.consumption_rates (single source of truth for non-blood items)
 * - L1-07: Remove analgesics_per_casualty: 4.0 (blended, not per-triage) — replaced by
 *          morphine_per_t1/t2/t3. Updated calculateRequiredStock() analgesics_doses accordingly.
 * - L1-08: Confirmed no national tourniquet/chest_seal/morphine overrides needed — NATO
 *          baseline applies for all current nations (ROU, US, DEU, GBR, POL).
 *
 * VERSION 2.1.0 CHANGES (February 22, 2026) — Phase 0-C Architecture:
 * - NL-01: Filled GERMAN (Bundeswehr) override — replaces placeholder.
 *   Bundeswehr point-of-injury TXA standard (since 2013), 1:1 PRBC:FFP ratio at 7.0 units/T1,
 *   21-day MDOS (2+5+14), Blutdepot network, push-based Versorgungsbataillon resupply doctrine.
 * - NL-02: Filled POLISH (Wojska Lądowe) override — replaces placeholder.
 *   6.5 units/T1 (above NATO 6.0, reflecting higher casualty intensity doctrine),
 *   18-day MDOS (2+4+12), Centralne Centrum Krwiodawstwa coordination,
 *   Brygada Logistyczna support structure, rapid expansion note.
 * - NL-03: Added BRITISH (British Army / DMS) override — new entry.
 *   6.0 units/T1 (NATO-equivalent), whole_blood_per_surgical=1.5 (CCAST protocol),
 *   23-day MDOS (2+5+16), Defence Blood Transfusion Service (DBTS),
 *   CCAST protocol, 101 Operational Sustainment Brigade logistics doctrine.
 * - NL-04: Updated AVAILABLE_NATIONAL_STANDARDS:
 *   GERMAN complete=true (was false/placeholder), description updated.
 *   POLISH complete=true (was false/placeholder), description updated.
 *   BRITISH entry added (new — did not exist).
 * 
 * ARCHITECTURE UPDATE v2.0.0:
 * - IMPORTS: PUSH_PACKAGES, MDOS_STANDARDS from classVIII.js (single source of truth)
 * - REMOVED: Duplicate push_packages definitions (use classVIII.js instead)
 * - REMOVED: Duplicate blood product structures (use classVIII.js instead)
 * - KEPT: National consumption rate OVERRIDES only
 * - KEPT: National thresholds and MDOS line structures
 * - RESULT: Thin file with only national differences
 * 
 * Implements:
 * - NL-001: National Consumption Rates Inheritance
 * - NL-002: Reverse Calculator (DOS → Stock)
 * - NL-006: Educational Documentation
 * 
 * @sources
 * - NATO AMedP-9.1 (Allied Medical Publication - Medical Estimate)
 * - NATO STANAG 2228 (Medical Supply Chain)
 * - NATO AJMedP-5 (Allied Joint Medical Planning)
 * - US FM 4-02.1 (Army Medical Logistics)
 * - Romanian Military Medical Doctrine (2023)
 * - Ukraine 2022-2024 combat medical data
 */

// ============================================================================
// IMPORTS FROM CLASS VIII (SINGLE SOURCE OF TRUTH)
// ============================================================================

// Import push packages and MDOS standards from classVIII.js
// DO NOT duplicate these - classVIII.js is the authoritative source
import { PUSH_PACKAGES, MDOS_STANDARDS, CONSUMPTION_RATES } from './classVIII';

// L1-01: APP_RULES 5.1 — icons from utils/icons.js only, no raw Unicode/emoji in code
import { FLAG_ICONS } from '../utils/icons';

// ============================================================================
// SECTION 1: NATO BASELINE (All nations inherit from this)
// ============================================================================

/**
 * NATO baseline logistics standards
 * This is the foundation - all national standards inherit from it
 * Values based on NATO AMedP-9.1 and STANAG 2228
 */
export const NATO_LOGISTICS_BASELINE = {
  /**
   * Consumption rates per casualty type per day
   * Based on NATO medical planning factors
   * SYNC: Must match CONSUMPTION_RATES in classVIII.js
   */
  consumption_rates: {
    // IV Fluids (liters per casualty) - SYNCED with classVIII.js
    iv_fluids_per_t1: 6.0,        // T1 (Immediate) - high fluid needs (classVIII: 6.0)
    iv_fluids_per_t2: 3.0,        // T2 (Delayed) - moderate fluid needs (classVIII: 3.0)
    iv_fluids_per_t3: 0.5,        // T3 (Minimal) - minimal fluid needs (classVIII: 0.5)
    iv_fluids_per_dnbi: 1.0,      // DNBI - standard fluid needs (classVIII: 1.0)
    
    // Blood products (units per casualty by triage) - SYNCED with classVIII.js
    prbc_per_t1: 6.0,             // T1: Massive transfusion likely (classVIII: 6)
    prbc_per_t2: 2.0,             // T2: Moderate transfusion (classVIII: 2)
    prbc_per_t3: 0.0,             // T3: Transfusion rare
    ffp_per_t1: 6.0,              // T1: 1:1 ratio with PRBC (classVIII: 6)
    ffp_per_t2: 2.0,              // T2: 1:1 ratio (classVIII: 2)
    ffp_per_t3: 0.0,              // T3: Rarely needed
    platelets_per_t1: 1.0,        // T1: MTP (classVIII: 1)
    platelets_per_t2: 0.0,        // T2: Rarely needed (classVIII: 0)
    
    // Legacy per-surgical rates (for backward compatibility)
    prbc_per_surgical: 4.0,       // Average across surgical cases
    ffp_per_surgical: 4.0,        // Fresh Frozen Plasma
    platelets_per_surgical: 0.5,  // Platelet units
    cryoprecipitate_per_surgical: 0.5, // Cryo units
    whole_blood_per_surgical: 0.0,     // Walking blood bank only
    
    // Pharmaceuticals (doses per casualty) - SYNCED with classVIII.js
    antibiotics_per_t1: 3.0,           // T1 (classVIII: 3)
    antibiotics_per_t2: 2.0,           // T2 (classVIII: 2)
    antibiotics_per_t3: 1.0,           // T3 (classVIII: 1)
    antibiotics_per_dnbi: 5.0,         // DNBI (classVIII: 5)
    antibiotics_per_casualty: 3.0,     // Legacy average

    // L1-06: Per-triage morphine/analgesic rates — replaces blended analgesics_per_casualty (L1-07)
    // Source: NATO AMedP-9.1, Ukraine 2022-2024 data
    morphine_per_t1: 2.0,             // T1 (Immediate) — pain control + sedation for surgical prep
    morphine_per_t2: 1.0,             // T2 (Delayed) — pain management
    morphine_per_t3: 0.0,             // T3 (Minimal) — ambulatory; oral analgesics only
    // MIGRATION: analgesics_per_casualty: 4.0 removed (blended, not per-triage).
    // Use morphine_per_t1/t2/t3 for new calculations. Legacy prbc_per_surgical paths unaffected.

    // L1-06: Per-triage tourniquet rates — enables national overrides and supplyChainModule tracking
    // Source: classVIII.js CONSUMPTION_RATES tourniquet field; Ukraine data confirms 2/T1
    tourniquet_per_t1: 2.0,           // T1: extremity hemorrhage control (2 per casualty average)
    tourniquet_per_t2: 0.5,           // T2: partial/preventive application
    tourniquet_per_t3: 0.0,           // T3: ambulatory — minimal tourniquet need

    // L1-06: Per-triage chest_seal rates
    // Source: classVIII.js CONSUMPTION_RATES chest_seals field; penetrating chest trauma rates
    chest_seal_per_t1: 2.0,           // T1: bilateral seal (entry + exit wound — 2 per casualty)
    chest_seal_per_t2: 0.5,           // T2: partial chest trauma
    chest_seal_per_t3: 0.0,           // T3: ambulatory — no chest seal required

    // Dressings per-triage rates (sets, not kg)
    // One dressing set = field dressing + hemostatic + bandage (~150g total)
    // Source: TCCC CoTCCC 2024 guidelines; casualty flow through POI → CCP → R1 → pre-op → post-op
    dressings_per_t1: 5.0,            // T1: POI + CCP repack + R1 + pre-op + post-op
    dressings_per_t2: 3.0,            // T2: POI + R1 + pre-surgical
    dressings_per_t3: 1.0,            // T3: R1 initial dressing only
    dressings_per_dnbi: 0.11,         // DNBI: vehicle accidents + skin infections only (~11% of DNBI)
                                      // Derived from dnbiRates.js DNBI_CATEGORIES wound-requiring fraction:
                                      // VEHICLE_ACCIDENTS(8%×0.80) + DISEASE/skin(1.8%) + OTHER(1.2%) + COLD_INJURY(1.4%)
    // NO national override — wound physiology is universal, only resuscitation doctrine varies by nation

    // Surgical kit per DCS procedure — 1 sterile pack per case (engineering identity, not clinical rate)
    // Source: US Army ATP 4-02.25 FST equipment tables; Role 2E/3 sterile pack consumption
    surgical_kit_per_case: 1.0,       // 1 kit consumed per DCS procedure regardless of wound type

    controlled_per_surgical: 8.0,       // Morphine/Ketamine per surgery
    anesthetics_per_surgical: 1.0,     // Per surgical procedure
    
    // Medical materiel (kg per casualty per day)
    dressings_kg_per_casualty: 0.5,
    surgical_supplies_kg_per_case: 2.0,
    general_medical_kg_per_casualty: 1.0,
    
    // Equipment consumption (per 100 casualties)
    ventilator_days_per_100: 15,       // Ventilator-days needed
    monitor_days_per_100: 40,          // Patient monitor-days
    oxygen_liters_per_t1_day: 1440,    // ~1L/min continuous
  },

  /**
   * DOS (Days of Supply) thresholds
   * Determines status color coding
   */
  dos_thresholds: {
    critical: 3,     // RED - Critical shortage
    minimum: 5,      // AMBER/YELLOW - Below minimum
    adequate: 10,    // GREEN - Adequate supply
    optimal: 15,     // BLUE - Optimal stockage
  },

  /**
   * MDOS Line structure (Medical Days of Supply)
   * Standard NATO 20-day package
   */
  mdos_lines: {
    LINE_1: {
      days: 3,
      purpose: 'Immediate combat use',
      location: 'With unit (Role 1)',
      resupply_priority: 'CRITICAL',
    },
    LINE_2: {
      days: 4,
      purpose: 'Resupply Line 1',
      location: 'Brigade Support Area (Role 2)',
      resupply_priority: 'HIGH',
    },
    LINE_3: {
      days: 13,
      purpose: 'Theater sustainment',
      location: 'Division/Corps (Role 2E/3)',
      resupply_priority: 'ROUTINE',
    },
    TOTAL: {
      days: 20,
      purpose: 'Full operational stockage',
    },
  },

  /**
   * REMOVED: Push package definitions (now imported from classVIII.js)
   * Use: import { PUSH_PACKAGES } from './classVIII'
   * 
   * Available packages:
   * - EMERGENCY_BLOOD: Rapid blood product delivery
   * - STANDARD_RESUPPLY: Routine operational resupply
   * - ROLE_1_DAILY: Daily battalion aid station push
   * - MASS_CASUALTY_PUSH / MASS_CASUALTY: MASCAL surge
   * - SURGICAL_RESUPPLY / SURGICAL_SUPPORT: Role 2 surgical
   * - TCCC_REPLENISHMENT: Individual IFAK restocking
   */

  /**
   * Resupply intervals by echelon
   * How often resupply should occur
   */
  resupply_intervals: {
    ROLE_1: {
      routine_hours: 24,
      emergency_hours: 6,
      mascal_hours: 2,
    },
    ROLE_2_BASIC: {
      routine_hours: 48,
      emergency_hours: 12,
      mascal_hours: 4,
    },
    ROLE_2_ENHANCED: {
      routine_hours: 72,
      emergency_hours: 24,
      mascal_hours: 8,
    },
    ROLE_3: {
      routine_hours: 96,
      emergency_hours: 48,
      mascal_hours: 12,
    },
  },

  /**
   * Critical items that trigger alerts
   * Items that must be tracked separately
   */
  critical_items: {
    blood_products: {
      shelf_life_days: { prbc: 42, ffp: 365, platelets: 5, whole_blood: 21 },
      minimum_stock_days: 2,
      alert_threshold_percent: 30,
    },
    controlled_substances: {
      requires_dual_custody: true,
      inventory_frequency_hours: 24,
      alert_threshold_percent: 20,
    },
    oxygen: {
      tracking_unit: 'liters',
      consumption_critical_hours: 12,
      alert_threshold_percent: 25,
    },
    ventilators: {
      maintenance_required_hours: 500,
      minimum_spare_percent: 20,
    },
  },

  /**
   * Wastage factors by supply category
   * Expected loss/damage/expiration rates
   */
  wastage_factors: {
    blood_products: 0.15,      // 15% wastage (expiration, damage)
    pharmaceuticals: 0.05,     // 5% wastage
    medical_materiel: 0.10,    // 10% wastage (damage, loss)
    equipment: 0.02,           // 2% equipment loss
  },

  /**
   * Ukraine combat data multipliers (2022-2024)
   * SYNC: Must match ukraineMultiplier in classVIII.js calculateDailyConsumption()
   * 
   * These factors adjust consumption based on actual combat medical data
   * from the Ukraine conflict, which showed higher consumption than NATO
   * planning factors in several categories.
   */
  ukraine_multipliers: {
    blood_products: 1.3,       // +30% - higher transfusion rates
    tourniquets: 1.5,          // +50% - more hemorrhage control needed
    hemostatic: 1.5,           // +50% - hemostatic dressings
    txa: 1.5,                  // +50% - TXA now standard for ALL trauma
  },
};


// ============================================================================
// SECTION 2: NATIONAL OVERRIDES
// ============================================================================

/**
 * National logistics overrides
 * Each nation specifies ONLY what differs from NATO baseline
 * This minimizes redundancy and makes differences explicit
 */
export const NATIONAL_LOGISTICS_OVERRIDES = {
  /**
   * ROMANIAN Armed Forces
   * Based on Romanian Military Medical Doctrine (2023)
   */
  ROMANIAN: {
    consumption_rates: {
      // Lower blood product usage (resource constraints)
      // Per-triage rates (25% lower than NATO)
      prbc_per_t1: 4.5,           // NATO: 6.0
      prbc_per_t2: 1.5,           // NATO: 2.0
      ffp_per_t1: 4.5,            // NATO: 6.0
      ffp_per_t2: 1.5,            // NATO: 2.0
      platelets_per_t1: 0.5,      // NATO: 1.0
      // Legacy per-surgical (for backward compatibility)
      prbc_per_surgical: 2.5,
      ffp_per_surgical: 2.5,
      // Higher reliance on crystalloids - same as NATO
      iv_fluids_per_t1: 6.0,
    },
    dos_thresholds: {
      // Romanian doctrine allows lower minimums
      critical: 2,
      minimum: 4,
      adequate: 7,
      optimal: 10,
    },
    mdos_lines: {
      // Romanian 15-day standard
      LINE_1: { days: 2 },
      LINE_2: { days: 3 },
      LINE_3: { days: 10 },
      TOTAL: { days: 15 },
    },
    // REMOVED: push_packages override (use PUSH_PACKAGES from classVIII.js)
    // Romanian-specific notes
    notes: {
      blood_source: 'National Blood Bank coordination required',
      controlled_substances: 'Ministry of Health approval for morphine allocation',
      resupply_constraint: 'Limited organic ground transport capacity',
    },
  },

  /**
   * United States Armed Forces
   * Based on FM 4-02.1 and DCBI protocols
   */
  US: {
    consumption_rates: {
      // Higher blood product usage (1:1:1 protocol, DCR)
      // Per-triage rates - higher than NATO for T1
      prbc_per_t1: 8.0,           // NATO: 6.0 - US aggressive MTP
      prbc_per_t2: 3.0,           // NATO: 2.0
      ffp_per_t1: 8.0,            // NATO: 6.0 - 1:1 ratio
      ffp_per_t2: 3.0,            // NATO: 2.0
      platelets_per_t1: 2.0,      // NATO: 1.0 - 1:1:1 protocol
      platelets_per_t2: 1.0,      // NATO: 0.0
      // Legacy per-surgical (for backward compatibility)
      prbc_per_surgical: 4.0,
      ffp_per_surgical: 4.0,
      platelets_per_surgical: 2.0,
      whole_blood_per_surgical: 2.0,  // Walking blood bank protocol
      // LOWER crystalloid usage (Damage Control Resuscitation)
      iv_fluids_per_t1: 3.0,      // NATO: 6.0 - DCR limits crystalloids
      iv_fluids_per_t2: 1.5,      // NATO: 3.0
      // Higher controlled substance allocation
      controlled_per_surgical: 12.0,
    },
    dos_thresholds: {
      // US doctrine requires higher minimums
      minimum: 7,
      adequate: 14,
      optimal: 21,
    },
    mdos_lines: {
      // US 30-day standard
      LINE_1: { days: 3 },
      LINE_2: { days: 7 },
      LINE_3: { days: 20 },
      TOTAL: { days: 30 },
    },
    // REMOVED: push_packages override (use PUSH_PACKAGES from classVIII.js)
    notes: {
      blood_source: 'Armed Services Blood Program (ASBP) coordination',
      resuscitation_protocol: 'Damage Control Resuscitation (DCR) standard',
      walking_blood_bank: 'Required at Role 2+ facilities',
    },
  },

  /**
   * German Bundeswehr (Heer)
   * NL-01: Based on Bundeswehr Sanitätsdienst doctrine and ZMedDBw (Zentrum für
   * Militärmedizin der Bundeswehr) medical planning standards.
   *
   * QM-6H-001: DOS threshold validation status — INTERNAL PLANNING VALUES.
   * No publicly available Bundeswehr Dienstvorschrift (ZDv/TF series) has been
   * found that explicitly codifies the critical/minimum/adequate/optimal day counts
   * below. The Bundeswehr operates two Sanitätsmateriallager (Epe + Krugau) and
   * supply authority recently transferred to Kommando Gesundheitsversorgung — but
   * no specific thresholds appear in any public doctrine document or journal source.
   * These values are defensible planning approximations based on:
   *   (1) Known Bundeswehr logistical depth doctrine (Heeresstruktur 5)
   *   (2) Higher stockage targets vs NATO baseline (consistent with Germany as
   *       host-nation logistics hub and peer-conflict doctrine)
   *   (3) Parallel to Polish doctrine (similar front-line nation stockage rationale)
   * Status: treat as internal planning values pending Bundeswehr expert validation.
   *
   * Key doctrinal features (confirmed from open sources):
   * - Point-of-injury TXA (Tranexamsäure) standard since 2013 — earlier adoption than NATO baseline
   * - 1:1 PRBC:FFP ratio (Damage Control Resuscitation, consistent with NATO MTP)
   * - Slightly higher per-T1 consumption (7.0 units) than NATO baseline (6.0)
   *   reflecting robust damage control surgery capability at forward echelons
   * - Bundeswehr Blutdepot network: pre-positioned blood products at Role 2+ facilities
   * - Push-based resupply via Versorgungsbataillon (organic to brigade)
   * - 21-day MDOS (2+5+14) — NATO standard plus 1 additional day in each line
   *   reflecting longer operational reach doctrine (Heeresstruktur 5)
   */
  GERMAN: {
    consumption_rates: {
      // 1:1 PRBC:FFP ratio at 7.0 units/T1 — slightly above NATO 6.0
      // Reflects early aggressive resuscitation doctrine and forward surgical capability
      prbc_per_t1: 7.0,           // NATO: 6.0 — Bundeswehr aggressive MTP
      prbc_per_t2: 2.0,           // NATO: 2.0 — same
      ffp_per_t1: 7.0,            // NATO: 6.0 — 1:1 ratio maintained
      ffp_per_t2: 2.0,            // NATO: 2.0 — same
      platelets_per_t1: 1.0,      // NATO: 1.0 — same (MTP standard)
      // Legacy per-surgical (for backward compatibility)
      prbc_per_surgical: 3.5,     // NATO: 4.0 — slightly lower (better pre-hospital haemostasis)
      ffp_per_surgical: 3.5,      // NATO: 4.0 — 1:1 maintained
      // TXA is standard point-of-injury; reflected in reduced secondary surgery rates
      // No crystalloid override: Bundeswehr follows NATO baseline (6.0 L/T1)
    },
    dos_thresholds: {
      // QM-6H-001: INTERNAL PLANNING VALUES — no public Bundeswehr doctrine source confirmed.
      // Plausible based on known logistical depth doctrine; pending Bundeswehr expert validation.
      // Bundeswehr requires higher minimum stockage than NATO baseline — longer supply lines,
      // emphasis on operational independence (Heeresstruktur 5 peer-conflict posture).
      critical: 5,      // NATO: 3 — higher minimum to prevent operational pause
      minimum: 10,      // NATO: 5 — robust buffer before mission impact
      adequate: 18,     // NATO: 10 — longer operational planning horizon
      optimal: 25,      // NATO: 15 — full operational independence target
    },
    mdos_lines: {
      // Bundeswehr 21-day MDOS standard (NATO + 1 day per line)
      // Reflects logistical depth doctrine for high-intensity peer conflict
      LINE_1: { days: 2, purpose: 'Sofortige Kampfversorgung', location: 'Bataillon (Sanitätszug)' },
      LINE_2: { days: 5, purpose: 'Nachschub Linie 1', location: 'Brigade (Sanitätskompanie / Versorgungsbataillon)' },
      LINE_3: { days: 14, purpose: 'Theaterversorgung', location: 'Division / Korps (Sanitätsbataillon / Versorgungsregiment)' },
      TOTAL: { days: 21, purpose: 'Vollständige operative Bevorratung' },
    },
    notes: {
      blood_source: 'Bundeswehr Blutdepot network — pre-positioned at Role 2+ (Sanitätskompanie and above)',
      resuscitation_protocol: 'Point-of-injury TXA (Tranexamsäure) standard since 2013; 1:1 PRBC:FFP damage control resuscitation',
      logistics_doctrine: 'Push-based resupply via Versorgungsbataillon (organic to brigade); Logistikbataillon at division; Versorgungsregiment at corps',
      txa_note: 'Bundeswehr adopted pre-hospital TXA earlier than most NATO nations — reduces T1 blood product requirements through earlier haemostasis',
    },
  },

  /**
   * Polish Armed Forces (Wojska Lądowe)
   * NL-02: Based on Wojskowa Służba Zdrowia (Military Medical Service) doctrine
   * and Polish Army medical planning standards (rapidly modernizing, 2022–2026).
   *
   * Key doctrinal features:
   * - 6.5 units/T1 PRBC and FFP (above NATO 6.0) — reflects higher expected casualty
   *   intensity on Poland's eastern flank and front-line nation warfighting posture
   * - Centralne Centrum Krwiodawstwa (Central Blood Donation Centre) coordination
   *   at corps level; Role 2+ blood program through Batalion Medyczny
   * - Brygada Logistyczna provides corps-level theater logistics
   * - 18-day MDOS (2+4+12) — slightly shorter than NATO 20 days reflecting
   *   forward positioning and shorter supply lines on home territory
   * - Logistics capacity under rapid expansion (2024–2026 defence investment program)
   * - Wojska Obrony Terytorialnej (WOT) supports rear-area medical evacuation
   */
  POLISH: {
    consumption_rates: {
      // Slightly above NATO baseline reflecting higher casualty intensity doctrine
      // Front-line nation posture requires more aggressive resuscitation planning
      prbc_per_t1: 6.5,           // NATO: 6.0 — higher planned consumption rate
      prbc_per_t2: 2.0,           // NATO: 2.0 — same
      ffp_per_t1: 6.5,            // NATO: 6.0 — 1:1 ratio maintained
      ffp_per_t2: 2.0,            // NATO: 2.0 — same
      platelets_per_t1: 1.0,      // NATO: 1.0 — same
      // Legacy per-surgical (for backward compatibility)
      prbc_per_surgical: 4.0,     // NATO: 4.0 — same
      ffp_per_surgical: 4.0,      // NATO: 4.0 — same
    },
    dos_thresholds: {
      // Similar to Bundeswehr — front-line nation requires higher minimums
      critical: 5,      // NATO: 3
      minimum: 10,      // NATO: 5
      adequate: 16,     // NATO: 10 — shorter than German (shorter supply lines)
      optimal: 22,      // NATO: 15
    },
    mdos_lines: {
      // Polish 18-day MDOS (2+4+12) — shorter than NATO 20 days
      // Forward positioning on home territory reduces required depth
      LINE_1: { days: 2, purpose: 'Natychmiastowe zabezpieczenie bojowe', location: 'Batalion (Pluton medyczny)' },
      LINE_2: { days: 4, purpose: 'Uzupełnienie Linii 1', location: 'Brygada (Kompania medyczna / Batalion logistyczny)' },
      LINE_3: { days: 12, purpose: 'Zabezpieczenie teatru działań', location: 'Dywizja / Korpus (Batalion medyczny / Brygada logistyczna)' },
      TOTAL: { days: 18, purpose: 'Pełne zaopatrzenie operacyjne' },
    },
    notes: {
      blood_source: 'Centralne Centrum Krwiodawstwa i Krwiolecznictwa coordination at corps level; Role 2+ blood program via Batalion Medyczny',
      logistics_doctrine: 'Brygada Logistyczna support structure at corps; Batalion Logistyczny organic to brigade; Pułk Logistyczny at division',
      expansion_note: 'Polish military logistics capacity under rapid expansion (2024–2026 defence investment program). Planning factors subject to revision.',
      wot_integration: 'Wojska Obrony Terytorialnej (WOT) supports rear-area evacuation and medical resupply on home territory',
    },
  },
  /**
   * British Army (British Army Land Forces / Defence Medical Services)
   * NL-03: Based on Defence Medical Services (DMS) doctrine, CCAST protocols,
   * and RLC / RAMC medical logistics standards.
   *
   * Key doctrinal features:
   * - 6.0 units/T1 PRBC and FFP — same as NATO baseline (British Army helped define it)
   * - whole_blood_per_surgical: 1.5 — CCAST (Critical Care Air Support Team) protocol
   *   uses warm fresh whole blood during strategic evacuation (Role 3 → Role 4)
   * - Defence Blood Transfusion Service (DBTS) provides pre-positioned blood at Role 2+
   * - DMS Walking Blood Bank (WBB) program formally institutionalized (cf. C-005 doctrinal note)
   * - 23-day MDOS (2+5+16) — deeper than NATO 20 days reflecting expeditionary doctrine
   *   and emphasis on sustained operations without host-nation support
   * - RLC Close Support Logistic Regiment at brigade; 101 Operational Sustainment Brigade
   *   at division/corps for theater-level logistics
   */
  BRITISH: {
    consumption_rates: {
      // NATO-equivalent PRBC/FFP ratio — British Army co-authored NATO MTP standards
      prbc_per_t1: 6.0,           // NATO: 6.0 — same (British contributed to this standard)
      prbc_per_t2: 2.0,           // NATO: 2.0 — same
      ffp_per_t1: 6.0,            // NATO: 6.0 — 1:1 ratio
      ffp_per_t2: 2.0,            // NATO: 2.0 — same
      platelets_per_t1: 1.0,      // NATO: 1.0 — same
      // Legacy per-surgical
      prbc_per_surgical: 4.0,     // NATO: 4.0 — same
      ffp_per_surgical: 4.0,      // NATO: 4.0 — same
      platelets_per_surgical: 0.5, // NATO: 0.5 — same
      // CCAST (Critical Care Air Support Team) whole blood protocol
      // Used during strategic evacuation (Role 3 → Role 4 STRATEVAC)
      whole_blood_per_surgical: 1.5, // NATO: 0.0 — CCAST STRATEVAC protocol
    },
    dos_thresholds: {
      // British expeditionary doctrine requires higher DOS depth
      // Reflects operations without guaranteed host-nation support
      critical: 5,      // NATO: 3
      minimum: 10,      // NATO: 5
      adequate: 18,     // NATO: 10
      optimal: 25,      // NATO: 15
    },
    mdos_lines: {
      // British 23-day MDOS (2+5+16) — expeditionary depth doctrine
      // Longer Line 3 reflects need for theater independence (no HNS assumed)
      LINE_1: { days: 2, purpose: 'Immediate combat medical support', location: 'Battalion (Regimental Aid Post / RAMC RAP team)' },
      LINE_2: { days: 5, purpose: 'Resupply Line 1 and Role 2 operations', location: 'Brigade (RAMC Field Ambulance / RLC Close Support Logistic Regiment)' },
      LINE_3: { days: 16, purpose: 'Theatre sustainment', location: 'Division / Corps (RAMC Medical Regiment / 101 Operational Sustainment Brigade)' },
      TOTAL: { days: 23, purpose: 'Full expeditionary operational stockage' },
    },
    notes: {
      blood_source: 'Defence Blood Transfusion Service (DBTS) — pre-positioned blood at Role 2+ via RAMC Field Ambulance and Medical Regiment',
      resuscitation_protocol: 'CCAST (Critical Care Air Support Team) protocol for STRATEVAC; DMS Walking Blood Bank (WBB) program formally institutionalized at Role 2+',
      logistics_doctrine: 'RLC Close Support Logistic Regiment organic to brigade; RLC Force Support Logistic Regiment at division; 101 Operational Sustainment Brigade for theatre-level logistics',
      wbb_note: 'British DMS WBB program: screened O-negative donors identified within units for emergency whole blood at Role 2. Documented in AMedP-1.1 operational appendices.',
    },
  },

  /**
   * FRENCH Armed Forces (Armée de Terre / Service de Santé des Armées)
   * NL-08: Based on SSA (Service de Santé des Armées) doctrine, CTSA (Centre de
   * Transfusion Sanguine des Armées) blood programme, and B.LOG logistics structure.
   *
   * Key doctrinal features:
   * - Consumption rates NATO-equivalent on all measurable parameters.
   *   France applies 1:1 RBC:plasma DCR doctrine — identical ratio to NATO baseline.
   * - CRITICAL DIFFERENTIATOR: France uses FLYP (Plasma Lyophilisé / French Lyophilized
   *   Plasma) instead of FFP. FLYP: room-temperature storage (2 years), universal blood
   *   group, reconstitution in <6 minutes. Enables true forward resuscitation at Role 1.
   *   This is a product substitution only — ffp_per_t1 rate unchanged at 6.0.
   *   Source: Martinaud et al. Transfusion 2013; Sailliol et al. Transfusion 2010;
   *     Daniel et al. J Trauma Acute Care Surg 2024. CTSA Clamart (CTSA = military blood bank).
   * - No US-style crystalloid restriction: French DCR follows NATO 6L/T1 IV fluid baseline.
   * - TXA: standard point-of-injury since at least 2013 (Sahel OPEX experience).
   *   Reduces secondary coagulopathy; no consumption rate implication.
   * - MDOS: NATO 20-day standard (3+4+13). No French doctrinal deviation found.
   * - DOS thresholds: NATO defaults. No French override found.
   * - Logistics doctrine: BSL (Bataillon de Soutien Logistique) at brigade;
   *   B.LOG (Brigade Logistique, activated March 26, 2024) commands 8 Train regiments,
   *   sustains 2 French brigades + 1 allied brigade at division level (target: 2027);
   *   GSC (Groupement de Soutien de Corps) at corps.
   */
  FRENCH: {
    // No consumption_rate overrides — all rates NATO-equivalent.
    // The FLYP substitution (product, not rate) is documented in notes below.
    // getNationalDifferences('FRENCH') will correctly return zero consumption_rate diffs.
    notes: {
      blood_source: 'Centre de Transfusion Sanguine des Armées (CTSA), Clamart — pre-positioned FLYP and RBC at Role 2+ via Compagnie Médicale and Groupement Médico-Chirurgical (GMC)',
      flyp_protocol: 'France uses FLYP (Plasma Lyophilisé) instead of FFP. 1:1 FLYP:RBC MTP ratio — same as NATO FFP:RBC ratio. Universal blood group, room-temp storage 2 years, <6 min reconstitution. Forward resuscitation from Role 1 upward. Source: CTSA Clamart / AMedP-1.1 appendix references.',
      txa_protocol: 'Tranexamic acid (TXA) standard point-of-injury since 2013 OPEX (Mali, CAR, Afghanistan). Reduces T1 coagulopathy; no rate override — reflected in baseline consumption factors.',
      logistics_doctrine: 'BSL (Bataillon de Soutien Logistique) organic to each BIA; Brigade Logistique (B.LOG, created March 2024) at corps/division with 8 Train regiments (7000 actifs); Groupement de Soutien de Corps (GSC) above. B.LOG target: sustain 2 French BIA + 1 allied brigade from 2027.',
      mdos_note: 'NATO 20-day MDOS standard (3+4+13). No French doctrinal deviation from NATO AMedP-9.1 found. French doctrine follows NATO resupply intervals per STANAG 2228.',
    },
  },

  // ============================================================================
  // V4-007: Five new NATO nations (April 9, 2026)
  // Data: doctrinal approximations based on open-source doctrine references.
  // All values are planning estimates — validated doctrine should be substituted
  // when national medical doctrine publications are available.
  // Each nation inherits NATO_LOGISTICS_BASELINE and overrides only where doctrine
  // clearly deviates from the NATO AMedP-9.1 standard.
  // ============================================================================

  /**
   * Turkish Land Forces (Kara Kuvvetleri)
   * V4-007 — doctrinal approximation.
   * Doctrine: KKTM (Kara Kuvvetleri Tatbikat Manuali).
   * CERRAH (Cerrahi) forward surgical teams forward of Role 2 — emphasis on early damage control.
   * Blood: slightly lower T1 PRBC (6.5) vs NATO baseline (6.0 already close; CERRAH teams consume
   *   more per casualty on average vs baseline NATO Role 2). Approximated from open-source KKTM references.
   * MDOS: 15-day standard (3+4+8). NATO-interoperable supply chain.
   */
  TURKISH: {
    consumption_rates: {
      prbc_per_t1: 6.5,  // NATO: 6.0 — CERRAH-forward slightly higher T1 blood demand
    },
    dos_thresholds: {
      critical: 3,
      minimum: 5,
      adequate: 10,
      optimal: 15,
    },
    mdos_lines: {
      LINE_1: { days: 3 },
      LINE_2: { days: 4 },
      LINE_3: { days: 8 },
      TOTAL: { days: 15 },
    },
    notes: {
      doctrine: 'KKTM doctrine. CERRAH forward surgical teams. NATO-interoperable.',
      blood_source: 'Turkish Military Blood Bank (Askeri Kan Bankasi). Universal donor pre-positioning at Role 2+.',
      mdos_note: '15-day MDOS standard (3+4+8). NATO-compatible resupply intervals per STANAG 2228.',
    },
  },

  /**
   * Norwegian Armed Forces (Forsvaret)
   * V4-007 — doctrinal approximation.
   * Doctrine: SANITETSBATALJON (Medical Battalion) doctrine; SMART (Scandinavian Military Advanced
   *   Resuscitation Techniques) protocol — cold-weather optimised.
   * Key deviation: cold-weather DNBI burden is handled via CBRN/terrain modifiers in dnbiModule.
   *   The logistics override captures the higher MDOS (20-day) and 7.0 PRBC/T1.
   * MDOS: 20-day standard (5+7+8). Extended due to Arctic resupply intervals.
   */
  NORWEGIAN: {
    consumption_rates: {
      prbc_per_t1: 7.0,  // NATO: 6.0 — SMART protocol includes cold-weather coagulopathy correction
    },
    dos_thresholds: {
      critical: 5,
      minimum: 8,
      adequate: 15,
      optimal: 20,
    },
    mdos_lines: {
      LINE_1: { days: 5 },
      LINE_2: { days: 7 },
      LINE_3: { days: 8 },
      TOTAL: { days: 20 },
    },
    notes: {
      doctrine: 'SANITETSBATALJON. SMART protocol (cold-weather resuscitation). 20-day MDOS.',
      blood_source: 'Forsvarets Sanitet blood bank. Pre-positioned frozen RBC for Arctic operations.',
      cold_weather_note: 'Cold-weather DNBI elevation (+15%) modelled via terrain/climate factors in dnbiModule; not a logistics rate change.',
    },
  },

  /**
   * Canadian Armed Forces (Forces Armees Canadiennes)
   * V4-007 — doctrinal approximation.
   * Doctrine: CFHS (Canadian Forces Health Services); ABCANZ alignment (Australia, Britain, Canada,
   *   New Zealand, US). SMART protocol.
   * Blood: 7.0 PRBC/T1 (ABCANZ alignment — closer to US DCR than NATO baseline).
   * MDOS: 21-day standard (3+5+13). Matches US pattern — ABCANZ interoperability.
   */
  CANADIAN: {
    consumption_rates: {
      prbc_per_t1: 7.0,  // NATO: 6.0 — ABCANZ/CFHS protocol (closer to US DCR)
    },
    dos_thresholds: {
      critical: 3,
      minimum: 5,
      adequate: 12,
      optimal: 21,
    },
    mdos_lines: {
      LINE_1: { days: 3 },
      LINE_2: { days: 5 },
      LINE_3: { days: 13 },
      TOTAL: { days: 21 },
    },
    notes: {
      doctrine: 'CFHS / ABCANZ alignment. SMART protocol. 21-day MDOS.',
      blood_source: 'Canadian Forces Blood Program. ABCANZ blood bank framework.',
      interop_note: 'ABCANZ alignment means strong interoperability with US/UK/AUS/NZ. DCR protocol applied.',
    },
  },

  /**
   * Netherlands Armed Forces (Koninklijke Landmacht)
   * V4-007 — doctrinal approximation.
   * Doctrine: KMC (Koninklijk Militair Clublid / Koninklijke Marechaussee Commandant); 1GNC
   *   (1st German/Netherlands Corps) joint framework with Germany.
   * Blood: 7.0 PRBC/T1 — SMART protocol (shared with NOR/CAN Scandinavian/ABCANZ alignment).
   * MDOS: 18-day standard (3+5+10). Intermediate between NATO 20 and ROMANIAN 15.
   */
  DUTCH: {
    consumption_rates: {
      prbc_per_t1: 7.0,  // NATO: 6.0 — KMC/1GNC protocol
    },
    dos_thresholds: {
      critical: 3,
      minimum: 5,
      adequate: 12,
      optimal: 18,
    },
    mdos_lines: {
      LINE_1: { days: 3 },
      LINE_2: { days: 5 },
      LINE_3: { days: 10 },
      TOTAL: { days: 18 },
    },
    notes: {
      doctrine: 'KMC. 1GNC joint framework with Germany. SMART protocol. 18-day MDOS.',
      blood_source: 'Militair Bloedtransfusiedienst. Pre-positioned at Role 2+ under 1GNC arrangement.',
      interop_note: '1GNC framework: logistics interoperable with GERMAN (DEU). Joint Role 3 arrangements.',
    },
  },

  /**
   * Italian Land Forces (Esercito Italiano)
   * V4-007 — doctrinal approximation.
   * Doctrine: COMFOP (Comando delle Forze Operative Terrestri). FST (Forward Surgical Team)
   *   emphasis — similar to CERRAH concept. AMIT (Aeromobile Italiana Traumatologica) aeromedical unit.
   * Blood: 7.0 PRBC/T1 — FST forward surgical emphasis drives slightly higher blood consumption vs NATO base.
   * MDOS: 15-day standard (3+4+8). Mediterranean operations pattern.
   */
  ITALIAN: {
    consumption_rates: {
      prbc_per_t1: 7.0,  // NATO: 6.0 — FST forward surgical emphasis
    },
    dos_thresholds: {
      critical: 3,
      minimum: 5,
      adequate: 10,
      optimal: 15,
    },
    mdos_lines: {
      LINE_1: { days: 3 },
      LINE_2: { days: 4 },
      LINE_3: { days: 8 },
      TOTAL: { days: 15 },
    },
    notes: {
      doctrine: 'COMFOP. FST forward surgical emphasis. AMIT aeromedical capability. 15-day MDOS.',
      blood_source: 'Centro Trasfusionale Militare. Pre-positioned RBC/FFP at Role 2+ via FST.',
      amit_note: 'AMIT (Aeromobile Italiana Traumatologica) provides primary aeromedical MEDEVAC capability.',
    },
  },

  /**
   * MULTINATIONAL Force
   * L1-05: Explicit entry — no overrides. Inherits NATO baseline entirely.
   * Rationale: MULTINATIONAL forces operate under NATO command with NATO logistics doctrine.
   * Per-nation logistics differences are a future enhancement (Section 7 of FIX_PLAN_LOGISTICS_DOS_v1_0).
   * Do NOT add consumption_rate overrides here — multinational forces use blended NATO planning factors.
   */
  MULTINATIONAL: {
    // Intentionally empty — full NATO baseline inheritance
    // Future: per-contributing-nation breakdown (see FIX_PLAN Section 7)
    notes: {
      doctrine: 'Inherits NATO baseline (AMedP-9.1). No national overrides.',
      future_note: 'Per-contributing-nation breakdown deferred to future enhancement.',
    },
  },
};
// ============================================================================

/**
 * Deep merge utility for nested objects
 * @param {Object} target - Base object
 * @param {Object} source - Override object
 * @returns {Object} Merged object
 */
const deepMerge = (target, source) => {
  const result = { ...target };
  
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  }
  
  return result;
};

/**
 * Get national logistics configuration
 * Merges NATO baseline with national overrides
 * 
 * @param {string} nationalStandard - 'NATO', 'ROMANIAN', 'US', 'GERMAN', 'POLISH'
 * @returns {Object} Complete logistics configuration for the nation
 * 
 * @example
 * const romanianLogistics = getNationalLogistics('ROMANIAN');
 * console.log(romanianLogistics.consumption_rates.prbc_per_surgical); // 2.5 (overridden)
 * console.log(romanianLogistics.consumption_rates.iv_fluids_per_t3);  // 0.5 (inherited from NATO)
 */
export const getNationalLogistics = (nationalStandard = 'NATO') => {
  // Start with NATO baseline
  let config = JSON.parse(JSON.stringify(NATO_LOGISTICS_BASELINE));
  
  // Apply national overrides if they exist
  const overrides = NATIONAL_LOGISTICS_OVERRIDES[nationalStandard];
  if (overrides) {
    config = deepMerge(config, overrides);
  }
  
  // Add metadata
  config._metadata = {
    standard: nationalStandard,
    baseline: 'NATO',
    has_overrides: !!overrides,
    generated_at: new Date().toISOString(),
  };
  
  return config;
};

/**
 * Get differences from NATO baseline for a national standard
 * Useful for displaying "What's different" in UI
 * 
 * @param {string} nationalStandard - National standard to compare
 * @returns {Object} Object containing only the differences
 */
export const getNationalDifferences = (nationalStandard) => {
  if (nationalStandard === 'NATO' || !NATIONAL_LOGISTICS_OVERRIDES[nationalStandard]) {
    return { differences: {}, count: 0 };
  }
  
  const overrides = NATIONAL_LOGISTICS_OVERRIDES[nationalStandard];
  const differences = [];
  
  // Flatten and compare
  const flattenDiffs = (obj, path = '') => {
    for (const key in obj) {
      const currentPath = path ? `${path}.${key}` : key;
      if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
        flattenDiffs(obj[key], currentPath);
      } else {
        // Find NATO baseline value
        const baselineValue = currentPath.split('.').reduce(
          (o, k) => (o && o[k] !== undefined) ? o[k] : undefined,
          NATO_LOGISTICS_BASELINE
        );
        
        if (baselineValue !== undefined) {
          differences.push({
            field: currentPath,
            nato_value: baselineValue,
            national_value: obj[key],
          });
        }
      }
    }
  };
  
  flattenDiffs(overrides);
  
  return {
    standard: nationalStandard,
    differences,
    count: differences.length,
  };
};


// ============================================================================
// SECTION 4: REVERSE CALCULATOR (DOS → Stock)
// ============================================================================

/**
 * Calculate required stock quantities from target DOS
 * This is the reverse of MDOS calculation
 * 
 * @param {number} targetDOS - Target days of supply
 * @param {Object} scenario - Scenario configuration with casualty estimates
 * @param {string} nationalStandard - National standard to use
 * @returns {Object} Required stock quantities by category
 * 
 * @example
 * const stock = calculateRequiredStock(7, { daily_casualties: { t1: 10, t2: 30 } }, 'ROMANIAN');
 */
export const calculateRequiredStock = (targetDOS, scenario, nationalStandard = 'NATO') => {
  const config = getNationalLogistics(nationalStandard);
  const rates = config.consumption_rates;
  const wastage = config.wastage_factors;
  const ukraineMultiplier = config.ukraine_multipliers || { blood_products: 1.0 };
  
  // Extract daily casualty estimates
  const daily = scenario.daily_casualties || {};
  const t1PerDay = daily.t1 || 0;
  const t2PerDay = daily.t2 || 0;
  const t3PerDay = daily.t3 || 0;
  const dnbiPerDay = daily.dnbi || 0;
  const surgicalPerDay = daily.surgical || Math.ceil((t1PerDay * 0.8) + (t2PerDay * 0.3));
  
  // Calculate base requirements (consumption * days)
  // SYNC: Must match classVIII.js calculateDailyConsumption logic
  const baseRequirements = {
    // IV Fluids (liters) - per triage category
    iv_fluids_liters: Math.ceil(
      (t1PerDay * rates.iv_fluids_per_t1 +
       t2PerDay * rates.iv_fluids_per_t2 +
       t3PerDay * rates.iv_fluids_per_t3 +
       dnbiPerDay * rates.iv_fluids_per_dnbi) * targetDOS
    ),
    
    // Blood products (units) - per triage category
    // SYNC: Apply ukraine_multipliers.blood_products like classVIII.js does
    prbc_units: Math.ceil(
      (t1PerDay * (rates.prbc_per_t1 || 6) +
       t2PerDay * (rates.prbc_per_t2 || 2) +
       t3PerDay * (rates.prbc_per_t3 || 0)) * targetDOS * ukraineMultiplier.blood_products
    ),
    ffp_units: Math.ceil(
      (t1PerDay * (rates.ffp_per_t1 || 6) +
       t2PerDay * (rates.ffp_per_t2 || 2) +
       t3PerDay * (rates.ffp_per_t3 || 0)) * targetDOS * ukraineMultiplier.blood_products
    ),
    platelets_units: Math.ceil(
      (t1PerDay * (rates.platelets_per_t1 || 1) +
       t2PerDay * (rates.platelets_per_t2 || 0)) * targetDOS
    ),
    cryoprecipitate_units: Math.ceil(surgicalPerDay * rates.cryoprecipitate_per_surgical * targetDOS),
    
    // Pharmaceuticals (doses) - per triage category
    antibiotics_doses: Math.ceil(
      (t1PerDay * (rates.antibiotics_per_t1 || 3) +
       t2PerDay * (rates.antibiotics_per_t2 || 2) +
       t3PerDay * (rates.antibiotics_per_t3 || 1) +
       dnbiPerDay * (rates.antibiotics_per_dnbi || 5)) * targetDOS
    ),
    // L1-07: Per-triage morphine rates replace blended analgesics_per_casualty
    analgesics_doses: Math.ceil(
      (t1PerDay * (rates.morphine_per_t1 || 2) +
       t2PerDay * (rates.morphine_per_t2 || 1) +
       t3PerDay * (rates.morphine_per_t3 || 0)) * targetDOS
    ),
    controlled_doses: Math.ceil(surgicalPerDay * rates.controlled_per_surgical * targetDOS),
    anesthetics_doses: Math.ceil(surgicalPerDay * rates.anesthetics_per_surgical * targetDOS),
    
    // Medical materiel (kg)
    dressings_kg: Math.ceil(
      (t1PerDay + t2PerDay + t3PerDay) * rates.dressings_kg_per_casualty * targetDOS
    ),
    surgical_supplies_kg: Math.ceil(surgicalPerDay * rates.surgical_supplies_kg_per_case * targetDOS),
    general_medical_kg: Math.ceil(
      (t1PerDay + t2PerDay + t3PerDay + dnbiPerDay) * rates.general_medical_kg_per_casualty * targetDOS
    ),
  };
  
  // Apply wastage factors to get required stock
  const requiredStock = {
    iv_fluids_liters: Math.ceil(baseRequirements.iv_fluids_liters * (1 + wastage.medical_materiel)),
    prbc_units: Math.ceil(baseRequirements.prbc_units * (1 + wastage.blood_products)),
    ffp_units: Math.ceil(baseRequirements.ffp_units * (1 + wastage.blood_products)),
    platelets_units: Math.ceil(baseRequirements.platelets_units * (1 + wastage.blood_products)),
    cryoprecipitate_units: Math.ceil(baseRequirements.cryoprecipitate_units * (1 + wastage.blood_products)),
    antibiotics_doses: Math.ceil(baseRequirements.antibiotics_doses * (1 + wastage.pharmaceuticals)),
    analgesics_doses: Math.ceil(baseRequirements.analgesics_doses * (1 + wastage.pharmaceuticals)),
    controlled_doses: Math.ceil(baseRequirements.controlled_doses * (1 + wastage.pharmaceuticals)),
    anesthetics_doses: Math.ceil(baseRequirements.anesthetics_doses * (1 + wastage.pharmaceuticals)),
    dressings_kg: Math.ceil(baseRequirements.dressings_kg * (1 + wastage.medical_materiel)),
    surgical_supplies_kg: Math.ceil(baseRequirements.surgical_supplies_kg * (1 + wastage.medical_materiel)),
    general_medical_kg: Math.ceil(baseRequirements.general_medical_kg * (1 + wastage.medical_materiel)),
  };
  
  return {
    target_dos: targetDOS,
    national_standard: nationalStandard,
    scenario_inputs: {
      t1_per_day: t1PerDay,
      t2_per_day: t2PerDay,
      t3_per_day: t3PerDay,
      dnbi_per_day: dnbiPerDay,
      surgical_per_day: surgicalPerDay,
    },
    base_requirements: baseRequirements,
    required_stock: requiredStock,
    wastage_applied: wastage,
  };
};


// ============================================================================
// SECTION 5: MDOS CALCULATION (Stock → DOS)
// ============================================================================

/**
 * Calculate MDOS from current stock levels
 * Returns the minimum DOS across all categories
 * 
 * FIX for T4-002: Returns 0 when stock is 0 (not 999)
 * 
 * @param {Object} currentStock - Current stock quantities
 * @param {Object} scenario - Scenario with daily consumption rates
 * @param {string} nationalStandard - National standard to use
 * @returns {Object} MDOS by category and overall
 */
export const calculateMDOS = (currentStock, scenario, nationalStandard = 'NATO') => {
  const config = getNationalLogistics(nationalStandard);
  const rates = config.consumption_rates;
  const ukraineMultiplier = config.ukraine_multipliers || { blood_products: 1.0 };
  
  // Extract daily casualty estimates
  const daily = scenario.daily_casualties || {};
  const t1PerDay = daily.t1 || 0;
  const t2PerDay = daily.t2 || 0;
  const t3PerDay = daily.t3 || 0;
  const dnbiPerDay = daily.dnbi || 0;
  const surgicalPerDay = daily.surgical || Math.ceil((t1PerDay * 0.8) + (t2PerDay * 0.3));
  const totalCasualtiesPerDay = t1PerDay + t2PerDay + t3PerDay + dnbiPerDay;
  
  // If no casualties expected, return max DOS
  if (totalCasualtiesPerDay === 0 && surgicalPerDay === 0) {
    return {
      overall_mdos: 999,
      status: 'NO_CONSUMPTION',
      message: 'No casualties projected - unlimited DOS',
      by_category: {},
    };
  }
  
  // Calculate daily consumption rates
  // SYNC: Must match classVIII.js calculateDailyConsumption logic (including Ukraine multipliers)
  const dailyConsumption = {
    iv_fluids_liters: (t1PerDay * rates.iv_fluids_per_t1) +
                      (t2PerDay * rates.iv_fluids_per_t2) +
                      (t3PerDay * rates.iv_fluids_per_t3) +
                      (dnbiPerDay * rates.iv_fluids_per_dnbi),
    // Blood products - per triage with Ukraine multiplier (SYNCED with classVIII.js)
    prbc_units: ((t1PerDay * (rates.prbc_per_t1 || 6)) +
                (t2PerDay * (rates.prbc_per_t2 || 2)) +
                (t3PerDay * (rates.prbc_per_t3 || 0))) * ukraineMultiplier.blood_products,
    ffp_units: ((t1PerDay * (rates.ffp_per_t1 || 6)) +
               (t2PerDay * (rates.ffp_per_t2 || 2)) +
               (t3PerDay * (rates.ffp_per_t3 || 0))) * ukraineMultiplier.blood_products,
    platelets_units: (t1PerDay * (rates.platelets_per_t1 || 1)) +
                     (t2PerDay * (rates.platelets_per_t2 || 0)),
    // Pharmaceuticals - per triage where available
    antibiotics_doses: (t1PerDay * (rates.antibiotics_per_t1 || 3)) +
                       (t2PerDay * (rates.antibiotics_per_t2 || 2)) +
                       (t3PerDay * (rates.antibiotics_per_t3 || 1)) +
                       (dnbiPerDay * (rates.antibiotics_per_dnbi || 5)),
    controlled_doses: surgicalPerDay * rates.controlled_per_surgical,
    dressings_kg: (t1PerDay + t2PerDay + t3PerDay) * rates.dressings_kg_per_casualty,
  };
  
  // Calculate DOS for each category
  const mdosByCategory = {};
  const dosValues = [];
  
  for (const [category, dailyRate] of Object.entries(dailyConsumption)) {
    if (dailyRate > 0) {
      const stock = currentStock[category] || 0;
      
      // FIX T4-002: If stock is 0, DOS is 0 (not infinity or 999)
      const dos = stock === 0 ? 0 : Math.floor(stock / dailyRate);
      
      mdosByCategory[category] = {
        current_stock: stock,
        daily_consumption: Math.round(dailyRate * 100) / 100,
        days_of_supply: dos,
      };
      
      dosValues.push(dos);
    }
  }
  
  // FIX T4-002: Overall MDOS is minimum, returns 0 if any category is 0
  // Only return 999 if dosValues is empty (no consumption calculated)
  const overallMDOS = dosValues.length > 0 ? Math.min(...dosValues) : 0;
  
  // Determine status based on thresholds
  const thresholds = config.dos_thresholds;
  let status, statusColor;
  
  if (overallMDOS === 0) {
    status = 'CRITICAL_ZERO';
    statusColor = 'BLACK';
  } else if (overallMDOS <= thresholds.critical) {
    status = 'CRITICAL';
    statusColor = 'RED';
  } else if (overallMDOS <= thresholds.minimum) {
    status = 'MINIMUM';
    statusColor = 'AMBER';
  } else if (overallMDOS <= thresholds.adequate) {
    status = 'ADEQUATE';
    statusColor = 'GREEN';
  } else {
    status = 'OPTIMAL';
    statusColor = 'BLUE';
  }
  
  // Find limiting factor(s)
  const limitingFactors = Object.entries(mdosByCategory)
    .filter(([_, data]) => data.days_of_supply === overallMDOS)
    .map(([category, _]) => category);
  
  return {
    overall_mdos: overallMDOS,
    status,
    status_color: statusColor,
    thresholds,
    limiting_factors: limitingFactors,
    by_category: mdosByCategory,
    national_standard: nationalStandard,
  };
};


// ============================================================================
// SECTION 6: RESUPPLY PLANNING
// ============================================================================

/**
 * Calculate when resupply is needed
 * 
 * @param {number} currentMDOS - Current MDOS
 * @param {number} operationDuration - Operation duration in days
 * @param {number} safetyBuffer - Days of safety buffer (default 2)
 * @returns {Object} Resupply planning information
 */
export const calculateResupplyNeeds = (currentMDOS, operationDuration, safetyBuffer = 2) => {
  const needsResupply = currentMDOS < operationDuration;
  
  if (!needsResupply) {
    return {
      needs_resupply: false,
      message: 'Adequate supplies for operation duration',
      shortfall_days: 0,
    };
  }
  
  const shortfallDays = operationDuration - currentMDOS;
  const resupplyDay = Math.max(1, currentMDOS - safetyBuffer);
  
  return {
    needs_resupply: true,
    shortfall_days: shortfallDays,
    resupply_by_day: resupplyDay,
    message: `Resupply required by Day ${resupplyDay}. ${shortfallDays}-day shortfall.`,
    urgency: shortfallDays > 5 ? 'ROUTINE' : shortfallDays > 2 ? 'HIGH' : 'CRITICAL',
  };
};

/**
 * Get push package contents with descriptions
 * NOW USES: PUSH_PACKAGES from classVIII.js (single source of truth)
 * 
 * @param {string} packageType - Package code (e.g. 'EMERGENCY_BLOOD', 'STANDARD_RESUPPLY')
 * @param {string} nationalStandard - National standard (kept for API compatibility, not used)
 * @returns {Object} Package details with contents
 */
export const getPushPackageDetails = (packageType, nationalStandard = 'NATO') => {
  // CRITICAL: Use PUSH_PACKAGES from classVIII.js (imported at top)
  const pkg = PUSH_PACKAGES[packageType];
  
  if (!pkg) {
    console.warn(`Push package ${packageType} not found in PUSH_PACKAGES`);
    return null;
  }
  
  return {
    type: packageType,
    label: pkg.label,                    // classVIII uses 'label' not 'name'
    description: pkg.description,
    supports_casualties: pkg.supports_casualties,
    weight_kg: pkg.weight_kg,
    volume_cubic_m: pkg.volume_cubic_m,
    delivery_priority: pkg.delivery_priority,
    contents: pkg.contents,
    notes: pkg.notes,
    // national_standard parameter kept for backward compatibility but not used
    // (packages are now universal from classVIII.js)
  };
};


// ============================================================================
// SECTION 7: EDUCATIONAL DOCUMENTATION
// ============================================================================

/**
 * Educational content for DOS concept
 * Implements NL-006 requirement
 */
export const DOS_EDUCATIONAL_CONTENT = {
  concept: {
    title: 'Days of Supply (DOS)',
    definition: 'DOS represents how long current medical supplies will sustain operations based on projected casualty rates.',
    nato_reference: 'NATO AMedP-9.1, Section 4.3',
  },
  
  mdos_lines: {
    title: 'Medical DOS (MDOS) Line Structure',
    description: 'NATO standard divides medical supplies into three lines based on proximity to combat and resupply priority.',
    lines: [
      {
        name: 'Line 1 (3 days)',
        location: 'With combat unit at Role 1',
        purpose: 'Immediate combat operations',
        resupply: 'Must be resupplied within 24 hours if depleted',
      },
      {
        name: 'Line 2 (4 days)',
        location: 'Brigade Support Area at Role 2',
        purpose: 'Resupply Line 1 and Role 2 operations',
        resupply: 'Resupplied from Line 3 within 48 hours',
      },
      {
        name: 'Line 3 (13 days)',
        location: 'Division/Corps level at Role 2E/3',
        purpose: 'Theater sustainment and reserve',
        resupply: 'Resupplied from strategic level',
      },
    ],
    total: '20 days total operational stockage (NATO standard)',
  },
  
  sources: {
    title: 'Doctrinal Sources',
    references: [
      {
        document: 'NATO AMedP-9.1',
        title: 'Allied Medical Publication - Medical Estimate',
        relevance: 'Primary source for NATO medical planning factors',
      },
      {
        document: 'NATO STANAG 2228',
        title: 'Medical Supply Chain',
        relevance: 'Standardization of medical logistics procedures',
      },
      {
        document: 'NATO AJMedP-5',
        title: 'Allied Joint Medical Planning',
        relevance: 'Joint planning considerations for medical support',
      },
      {
        document: 'US FM 4-02.1',
        title: 'Army Medical Logistics',
        relevance: 'US Army medical supply procedures',
      },
      {
        document: 'Romanian Military Medical Doctrine',
        title: 'Doctrina Medicală Militară (2023)',
        relevance: 'Romanian Armed Forces medical standards',
      },
    ],
  },
  
  consumption_factors: {
    title: 'Consumption Rate Factors',
    description: 'Factors that affect medical supply consumption:',
    factors: [
      'Casualty rate (higher intensity = more consumption)',
      'Triage category mix (T1 casualties consume more)',
      'Surgical case rate (affects blood and controlled substances)',
      'DNBI rate (climate and operational conditions)',
      'Evacuation policy (longer holding = more consumption)',
      'National medical protocols (1:1:1 vs crystalloid resuscitation)',
    ],
  },
  
  ukraine_lessons: {
    title: 'Ukraine 2022-2024 Lessons',
    description: 'Combat data from Ukraine has informed consumption rate adjustments:',
    lessons: [
      'Higher tourniquet usage than historical planning factors',
      'Increased blood product requirements due to explosive injuries',
      'Longer evacuation times requiring more pre-hospital supplies',
      'Higher surgical case rates at Role 2',
      'Significant DNBI burden from weather and hygiene conditions',
    ],
  },
};


// ============================================================================
// SECTION 8: AVAILABLE NATIONAL STANDARDS
// ============================================================================

/**
 * List of available national standards
 * Used for dropdown/selector UI
 * L1-02: FLAG_ICONS from utils/icons.js — APP_RULES 5.1 (no raw emoji in code)
 * L1-03: shortLabel values match nationalStandards.js exactly
 * L1-04: MULTINATIONAL entry added
 */
export const AVAILABLE_NATIONAL_STANDARDS = [
  {
    code: 'NATO',
    shortLabel: 'NATO',
    name: 'NATO Standard',
    description: 'NATO baseline (AMedP-9.1)',
    flag: FLAG_ICONS.NATO,
    complete: true,
  },
  {
    code: 'ROMANIAN',
    shortLabel: 'ROU',
    name: 'Romanian Armed Forces',
    description: 'Forțele Armate Române',
    flag: FLAG_ICONS.ROMANIAN,
    complete: true,
  },
  {
    code: 'US',
    shortLabel: 'US',
    name: 'United States Armed Forces',
    description: 'US Army/USMC (FM 4-02.1)',
    flag: FLAG_ICONS.US,
    complete: true,
  },
  {
    // NL-04: GERMAN complete=true — placeholder replaced with full Bundeswehr doctrine data
    code: 'GERMAN',
    shortLabel: 'DEU',
    name: 'German Bundeswehr (Heer)',
    description: 'Bundeswehr Heer — Sanitätsdienst / Versorgungsbataillon doctrine',
    flag: FLAG_ICONS.GERMAN,
    complete: true,
  },
  {
    // NL-04: POLISH complete=true — placeholder replaced with full Wojska Lądowe doctrine data
    code: 'POLISH',
    shortLabel: 'POL',
    name: 'Polish Armed Forces (Wojska Lądowe)',
    description: 'Wojsko Polskie — Wojskowa Służba Zdrowia / Brygada Logistyczna doctrine',
    flag: FLAG_ICONS.POLISH,
    complete: true,
  },
  {
    // NL-04: BRITISH added — new entry, did not exist in v2.0.0
    code: 'BRITISH',
    shortLabel: 'GBR',
    name: 'British Army (Land Forces)',
    description: 'British Army DMS — CCAST / DBTS / RLC logistics doctrine',
    flag: FLAG_ICONS.BRITISH,
    complete: true,
  },
  {
    // NL-09: FRENCH added — Armée de Terre / Service de Santé des Armées
    // Consumption rates NATO-equivalent; key differentiator is FLYP (lyophilized plasma)
    // replacing FFP — same 1:1 ratio, different product (room-temp, universal blood group).
    code: 'FRENCH',
    shortLabel: 'FRA',
    name: 'Armée de Terre (French Army)',
    description: 'SSA / CTSA — FLYP lyophilized plasma / Brigade Logistique (B.LOG) doctrine',
    flag: FLAG_ICONS.FRENCH,
    complete: true,
  },
  // V4-007: Five new NATO nations (April 9, 2026)
  // No flag field (no-emoji rule). Data: doctrinal approximations.
  {
    code: 'TURKISH',
    shortLabel: 'TUR',
    name: 'Turkish Land Forces',
    description: 'KKTM doctrine. 15-day MDOS. CERRAH forward surgical teams. NATO-interoperable.',
    complete: true,
  },
  {
    code: 'NORWEGIAN',
    shortLabel: 'NOR',
    name: 'Norwegian Armed Forces',
    description: 'SANITETSBATALJON. 20-day MDOS. Cold weather DNBI +15%. SMART protocol.',
    complete: true,
  },
  {
    code: 'CANADIAN',
    shortLabel: 'CAN',
    name: 'Canadian Armed Forces',
    description: 'CFHS / ABCANZ alignment. 21-day MDOS. SMART protocol.',
    complete: true,
  },
  {
    code: 'DUTCH',
    shortLabel: 'NLD',
    name: 'Netherlands Armed Forces',
    description: 'KMC. 18-day MDOS. 1GNC/German framework. SMART protocol.',
    complete: true,
  },
  {
    code: 'ITALIAN',
    shortLabel: 'ITA',
    name: 'Italian Land Forces',
    description: 'COMFOP. 15-day MDOS. FST emphasis. AMIT aeromedical capability.',
    complete: true,
  },
  {
    // L1-04: MULTINATIONAL added — matches MULTINATIONAL entry in nationalStandards.js
    code: 'MULTINATIONAL',
    shortLabel: 'MN',
    name: 'Multinational Force',
    description: 'Multinational — inherits NATO baseline logistics',
    flag: FLAG_ICONS.MULTINATIONAL,
    complete: true,
    note: 'Inherits NATO baseline — no logistics overrides',
  },
];


// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named export object for ESLint compliance (import/no-anonymous-default-export)
const nationalLogisticsModule = {
  NATO_LOGISTICS_BASELINE,
  NATIONAL_LOGISTICS_OVERRIDES,
  getNationalLogistics,
  getNationalDifferences,
  calculateRequiredStock,
  calculateMDOS,
  calculateResupplyNeeds,
  getPushPackageDetails,
  DOS_EDUCATIONAL_CONTENT,
  AVAILABLE_NATIONAL_STANDARDS,
  // Re-export from classVIII.js for convenience
  PUSH_PACKAGES,
  MDOS_STANDARDS,
  CONSUMPTION_RATES
};

export default nationalLogisticsModule;
